import React, { useEffect, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, AlertTriangle } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { CopyButton } from "@/components/common";
import Uploader from "@/components/Uploader";

export default function AddUsdt() {
  const nav = useNavigate();
  const [settings, setSettings] = useState({});
  const [network, setNetwork] = useState("TRC20");
  const [amount, setAmount] = useState("");
  const [txid, setTxid] = useState("");
  const [fileId, setFileId] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/settings/public").then((r) => setSettings(r.data)).catch(() => {}); }, []);

  const rawAddr = network === "TRC20" ? settings.trc20_address : settings.bep20_address;
  const netActive = network === "TRC20" ? settings.trc20_active !== false : settings.bep20_active !== false;
  const addr = netActive ? rawAddr : null;
  const unavailable = !!rawAddr && !netActive;

  const submit = async (e) => {
    e.preventDefault();
    if (!addr) { toast.error(`The ${network} network is currently unavailable for deposits`); return; }
    if (!fileId) { toast.error("Transaction screenshot is required"); return; }
    setLoading(true);
    try {
      await api.post("/customer/deposits", { network, amount, txid, file_id: fileId });
      toast.success("Deposit submitted for verification");
      nav("/app/deposits");
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">Add USDT</h1>
        <p className="text-sm text-slate-500 mt-1">Send USDT to the verified address, then submit your transaction details.</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-5">
        <div className="flex gap-2 p-1 bg-slate-100 rounded-xl w-full">
          {["TRC20", "BEP20"].map((n) => (
            <button key={n} onClick={() => setNetwork(n)} data-testid={`deposit-network-${n.toLowerCase()}-tab`}
              className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition ${network === n ? "bg-white text-blue-700 shadow-sm" : "text-slate-500"}`}>
              {n} {n === "TRC20" ? "(Tron)" : "(BNB Chain)"}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl p-3 text-amber-800 text-sm">
          <AlertTriangle className="w-5 h-5 shrink-0" />
          Send only USDT on the <strong>{network}</strong> network to this address. Wrong-network transfers may be lost.
        </div>

        {addr ? (
          <div className="flex flex-col sm:flex-row gap-5 items-center">
            <div className="bg-white p-3 rounded-xl border border-slate-200">
              <QRCodeCanvas value={addr} size={132} data-testid="deposit-qr-code" />
            </div>
            <div className="flex-1 w-full">
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Receiving address ({network})</div>
              <div className="font-mono text-sm break-all bg-slate-50 border border-slate-200 rounded-xl p-3" data-testid="deposit-address-value">{addr}</div>
              <div className="mt-2"><CopyButton value={addr} testid="deposit-address-copy-button" label="Copy address" /></div>
            </div>
          </div>
        ) : (
          <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-xl p-3" data-testid="no-address-configured">
            {unavailable
              ? `The ${network} network is temporarily unavailable. Please use another network or contact support.`
              : `The ${network} receiving address is not configured yet. Please contact support before depositing.`}
          </div>
        )}
      </div>

      <form onSubmit={submit} className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4">
        <label className="block">
          <span className="text-sm font-medium text-slate-700">Deposit amount (USDT)</span>
          <input type="number" step="0.000001" min="0" required value={amount} onChange={(e) => setAmount(e.target.value)}
            data-testid="deposit-amount-input" className="mt-1.5 w-full px-4 py-2.5 rounded-xl border border-slate-300 focus:border-blue-500 outline-none font-mono" placeholder="0.00" />
        </label>
        <label className="block">
          <span className="text-sm font-medium text-slate-700">Transaction hash (TXID)</span>
          <input required value={txid} onChange={(e) => setTxid(e.target.value)}
            data-testid="deposit-txid-input" className="mt-1.5 w-full px-4 py-2.5 rounded-xl border border-slate-300 focus:border-blue-500 outline-none font-mono text-sm" placeholder="0x…" />
        </label>
        <div>
          <span className="text-sm font-medium text-slate-700">Transaction screenshot (required)</span>
          <div className="mt-1.5"><Uploader fileId={fileId} onUploaded={setFileId} testid="deposit-screenshot-upload" /></div>
        </div>
        <button disabled={loading || !addr} data-testid="deposit-submit-button"
          className="w-full px-4 py-3 rounded-xl bg-blue-600 text-white font-semibold hover:bg-blue-700 transition disabled:opacity-60 inline-flex items-center justify-center gap-2">
          {loading && <Loader2 className="w-4 h-4 animate-spin" />} Submit deposit for verification
        </button>
        <p className="text-xs text-slate-400 text-center">A screenshot alone never credits USDT. Staff verify your transaction before crediting.</p>
      </form>
    </div>
  );
}
